'use client'

import { AlertTriangle, ExternalLinkIcon, Loader2Icon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import Link from 'next/link'
import { useMemo } from 'react'

import {
  SettingsCard,
  SettingsCardHeader,
  SettingsRow,
  SettingsRowGroup,
} from '@/components/dialog/settings/settings-card'
import { SETTINGS_PANEL_CLASS } from '@/components/dialog/settings/settings-card-styles'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  type CapabilityHighlight,
  capabilityHighlights,
} from '@/features/billing/lib/capability-highlights'
import { daysUntil, formatPeriodEnd } from '@/features/billing/lib/format'
import CreditsSection from '@/features/settings/billing/credits/credits-section'
import {
  describeSubscription,
  type SubscriptionState,
} from '@/features/settings/billing/subscription-status'
import { BILLING_ROUTE, BILLING_SETTINGS_ROUTE } from '@/lib/billing/routes'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'
import { useOpenBillingPortal, useSubscription } from '@/service/billing.hook'

// Only the count rows read cleanly as a one-line "what you have" summary; the
// bool rows (analytics streams, risk sim) are plan-comparison material and
// already live on the plans page.
const SUMMARY_ROWS: ReadonlyArray<CapabilityHighlight['key']> = [
  'tradingAccounts',
  'accountSharing',
  'venues',
  'algoTypes',
]

const STATE_PILL: Record<SubscriptionState, string> = {
  none: 'bg-foreground/10 text-foreground/70',
  comped: 'bg-ox-cyan/15 text-foreground',
  active: 'bg-chart-mint/15 text-chart-mint',
  trialing: 'bg-ox-cyan/15 text-foreground',
  pastDue: 'bg-ox-gold/15 text-ox-gold',
  canceling: 'bg-ox-gold/15 text-ox-gold',
}

export default function BillingPanel() {
  const t = useTranslations('settings.billing')
  const tBilling = useTranslations('billing')
  const locale = useLocale()
  const { data: sub, isLoading, isError } = useSubscription()

  const portal = useOpenBillingPortal({
    onSuccess: ({ portalUrl }) => {
      window.location.href = portalUrl
    },
    onError: () => toast.error(tBilling('portalError')),
  })

  const openPortal = () => {
    // Return to this tab, not the plans page — the user came here to manage,
    // and the badge/gates refetch on focus once they're back.
    portal.mutate({ returnUrl: `${window.location.origin}${BILLING_SETTINGS_ROUTE}` })
  }

  const view = sub ? describeSubscription(sub) : null
  const periodEnd = sub ? formatPeriodEnd(sub.currentPeriodEnd, locale) : null
  const summary = useMemo(() => {
    if (!sub?.capabilities) return []
    return capabilityHighlights(sub.capabilities).filter(
      (row): row is Extract<CapabilityHighlight, { kind: 'count' }> =>
        row.kind === 'count' && SUMMARY_ROWS.includes(row.key)
    )
  }, [sub?.capabilities])

  const statusLine = (() => {
    if (!sub || !view) return null
    switch (view.state) {
      case 'trialing':
        return tBilling('trialDaysLeft', {
          plan: formatFirstLetterToUpperCase(sub.planLabel),
          days: daysUntil(sub.currentPeriodEnd),
        })
      case 'canceling':
        return periodEnd ? tBilling('cancelsOn', { date: periodEnd }) : null
      case 'active':
        return periodEnd ? tBilling('renewsOn', { date: periodEnd }) : null
      case 'pastDue':
        return t('pastDueBody')
      case 'comped':
        return t('compedBody')
      case 'none':
        return t('noSubscriptionBody')
    }
  })()

  // The portal buttons are always rendered so the user learns where to manage
  // billing from; when there is no Stripe sub behind the plan they're disabled
  // and the tooltip says why.
  const portalAvailable = view?.portalAvailable ?? false
  const disabledReason = (() => {
    if (!view) return null
    if (view.state === 'comped') return t('portalUnavailableComped')
    if (!view.portalAvailable) return t('portalUnavailableFree')
    return null
  })()

  return (
    <div className={SETTINGS_PANEL_CLASS}>
      {/* Same grammar as the other tabs: a SettingsCardHeader with its one action on the
          right, then SettingsRows — the plan is a row (name, status line, state badge) and
          each capability a label/value row, like General's preferences. */}
      <SettingsCard>
        <div className="flex w-full items-center justify-between gap-4">
          <SettingsCardHeader
            title={t('currentPlanTitle')}
            description={t('currentPlanDescription')}
          />
          {view && (
            <Button asChild variant={view.state === 'none' ? 'cta' : 'secondary'} size="sm">
              <Link href={BILLING_ROUTE}>
                {view.state === 'none' ? tBilling('upgrade') : t('viewPlans')}
              </Link>
            </Button>
          )}
        </div>

        {isLoading && (
          <p className="text-foreground/60 flex items-center gap-2 text-sm tracking-tight">
            <Loader2Icon className="size-4 animate-spin" />
            {t('loading')}
          </p>
        )}

        {isError && (
          <p className="text-foreground/60 flex items-center gap-2 text-sm tracking-tight">
            <AlertTriangle className="text-ox-gold size-4 shrink-0" />
            {tBilling('unavailableBody')}
          </p>
        )}

        {sub && view && (
          <SettingsRowGroup>
            <SettingsRow
              label={formatFirstLetterToUpperCase(sub.planLabel || 'Essential')}
              description={
                statusLine && (
                  <span
                    className={cn(
                      'flex items-start gap-1.5',
                      view.state === 'pastDue' && 'text-ox-gold'
                    )}
                  >
                    {view.state === 'pastDue' && (
                      <AlertTriangle className="mt-px size-3.5 shrink-0" />
                    )}
                    {statusLine}
                  </span>
                )
              }
              control={
                <span className={cn('px-2 py-0.5 text-xs font-medium', STATE_PILL[view.state])}>
                  {t(`state.${view.state}`)}
                </span>
              }
            />
            {summary.map((row) => (
              <SettingsRow
                key={row.key}
                label={tBilling(row.labelKey as 'features.tradingAccounts')}
                control={
                  <span className="text-foreground text-sm font-medium tabular-nums">
                    {row.count}
                  </span>
                }
              />
            ))}
          </SettingsRowGroup>
        )}
      </SettingsCard>

      {/* Header + one action, like Devices' session card. Without a Stripe subscription the
          button stays (so the user learns where billing lives) but disabled, with why. */}
      <SettingsCard>
        <div className="flex w-full items-center justify-between gap-4">
          <SettingsCardHeader title={t('manageTitle')} description={t('manageDescription')} />
          <PortalButton
            label={tBilling('manageSubscription')}
            disabled={!portalAvailable || isLoading}
            disabledReason={disabledReason}
            pending={portal.isPending}
            onClick={openPortal}
          />
        </div>
        {disabledReason && (
          <p className="text-foreground/60 text-xs tracking-tight">{disabledReason}</p>
        )}
      </SettingsCard>

      {/* Cancelling is its own row card (like Devices' "Log out"), and only when there is a
          Stripe subscription to cancel. It goes through the same portal. */}
      {portalAvailable && (
        <SettingsCard layout="row">
          <SettingsCardHeader title={t('cancelSubscription')} description={t('portalHint')} />
          <PortalButton
            label={t('cancelSubscription')}
            disabled={isLoading}
            disabledReason={null}
            pending={portal.isPending}
            onClick={openPortal}
          />
        </SettingsCard>
      )}

      <CreditsSection />
    </div>
  )
}

function PortalButton({
  label,
  disabled,
  disabledReason,
  pending,
  onClick,
}: {
  label: string
  disabled: boolean
  disabledReason: string | null
  pending: boolean
  onClick: () => void
}) {
  const button = (
    <Button variant="secondary" size="sm" disabled={disabled || pending} onClick={onClick}>
      {pending ? (
        <Loader2Icon className="size-3.5 animate-spin" />
      ) : (
        <ExternalLinkIcon className="size-3.5" />
      )}
      {label}
    </Button>
  )
  if (!disabled || !disabledReason) return button
  // A disabled button swallows pointer events, so wrap it for the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  )
}
